/**
 * Google Analytics 4 (gtag) + Meta Pixel (fbq) helper.
 *
 * Loaders live in public/index.html and are injected into every prerendered
 * snapshot. This module provides event helpers plus a single global
 * click-delegation listener that tracks EVERY interactive element, so we never
 * have to wire onClick into 40+ button locations.
 *
 * Safe in SSR/non-browser contexts (no-ops when window/gtag/fbq missing).
 */

const GA4_ID = 'G-XQCNLWB3NF';

export const trackEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', eventName, params);
  } catch (_) {
    /* swallow analytics errors — never break UX */
  }
};

/**
 * Fire a Meta Pixel event.
 * @param {string} eventName Standard event (e.g. 'Contact', 'Lead') or custom event.
 * @param {object} params Optional event parameters.
 * @param {boolean} isCustom Use trackCustom instead of track.
 */
export const trackPixel = (eventName, params = {}, isCustom = false) => {
  if (typeof window === 'undefined') return;
  if (typeof window.fbq !== 'function') return;
  try {
    window.fbq(isCustom ? 'trackCustom' : 'track', eventName, params);
  } catch (_) {
    /* ignore */
  }
};

export const trackWhatsAppClick = (source = 'unknown', extra = {}) => {
  trackEvent('whatsapp_click', { source, method: 'whatsapp', ...extra });
  trackPixel('Contact', { content_name: source, method: 'whatsapp' });
};

export const trackLeadSubmit = (source = 'unknown', service = '') => {
  const value = 4500; // Guard Pro baseline, used for conversion value estimation
  trackEvent('generate_lead', { source, service, currency: 'MYR', value });
  trackPixel('Lead', {
    content_name: source,
    content_category: service || 'general',
    currency: 'MYR',
    value,
  });
};

export const trackPageView = (path) => {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag === 'function') {
    try {
      window.gtag('config', GA4_ID, { page_path: path, anonymize_ip: true });
    } catch (_) { /* ignore */ }
  }
  if (typeof window.fbq === 'function') {
    try {
      window.fbq('track', 'PageView');
    } catch (_) { /* ignore */ }
  }
};

/** Best-effort human-readable label for any clicked element. */
const labelFor = (el) =>
  el.getAttribute('data-testid') ||
  el.getAttribute('data-track') ||
  el.getAttribute('aria-label') ||
  (el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60) ||
  'unlabelled';

const PRICE_TO_VALUE = (price = '') => {
  const digits = String(price).replace(/[^\d]/g, '');
  return digits ? Number(digits) : undefined;
};

let installed = false;

/**
 * Install one global click listener covering every trackable control:
 *   WhatsApp links, phone links, email links, package CTAs, share buttons,
 *   internal navigation and any element marked with data-track.
 */
export const installGlobalAnalyticsListeners = () => {
  if (installed) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;

  document.addEventListener(
    'click',
    (e) => {
      const target = e.target;
      if (!target?.closest) return;

      const el = target.closest('a, button, [data-track]');
      if (!el) return;

      const href = el.getAttribute?.('href') || '';
      const label = labelFor(el);
      const marked = el.getAttribute('data-track');

      // 0. Explicitly marked controls win (share buttons, accordions, filters)
      if (marked) {
        trackEvent(marked, { source: label });
        trackPixel(marked, { content_name: label }, true);
        return;
      }

      // 1. WhatsApp
      if (href.includes('wa.me/')) {
        const pkgName = el.getAttribute('data-pkg-name');
        if (pkgName) {
          const value = PRICE_TO_VALUE(el.getAttribute('data-pkg-price'));
          trackEvent('select_package', { source: label, package: pkgName, currency: 'MYR', value });
          trackPixel('InitiateCheckout', {
            content_name: pkgName,
            content_category: 'cctv-package',
            currency: 'MYR',
            value,
          });
        }
        trackWhatsAppClick(label, { href });
        return;
      }

      // 2. Phone
      if (href.startsWith('tel:')) {
        trackEvent('phone_click', { source: label, method: 'phone' });
        trackPixel('Contact', { content_name: label, method: 'phone' });
        return;
      }

      // 3. Email
      if (href.startsWith('mailto:')) {
        trackEvent('email_click', { source: label, method: 'email' });
        trackPixel('Contact', { content_name: label, method: 'email' });
        return;
      }

      // 4. Internal navigation and in-page anchors
      if (href.startsWith('#') || href.startsWith('/')) {
        trackEvent('nav_click', { source: label, destination: href });
        trackPixel('NavClick', { content_name: label, destination: href }, true);
      }
    },
    { passive: true },
  );
};
